import { useEffect, useRef, useState } from "react";
import { Pause, Play, RotateCcw, SkipBack, SkipForward } from "lucide-react";
import YouTubePracticePlayer from "@/components/music/YouTubePracticePlayer";
import usePracticeChordSync from "@/hooks/usePracticeChordSync";

const formatTime = (seconds = 0) => {
  const safe = Math.max(0, Number(seconds) || 0);
  return `${Math.floor(safe / 60)}:${String(Math.floor(safe % 60)).padStart(2, "0")}`;
};

const AdminPracticePreview = ({ video, chords, duration, onSyncChange }) => {
  const playerRef = useRef(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [playerError, setPlayerError] = useState("");
  const sync = usePracticeChordSync(playerRef, chords, { duration });
  const controlsDisabled = !playerReady || Boolean(playerError);

  useEffect(() => {
    setPlayerReady(false);
    setPlayerError("");
  }, [video?.youtube_video_id]);

  useEffect(() => {
    onSyncChange?.({
      currentTime: sync.currentTime,
      duration: sync.duration,
      currentIndex: sync.currentIndex,
      currentChord: sync.currentChord,
      nextChord: sync.nextChord,
      isPlaying: sync.isPlaying,
      seekTo: sync.seekTo,
      togglePlayback: () => (sync.isPlaying ? playerRef.current?.pause?.() : playerRef.current?.play?.()),
    });
  }, [onSyncChange, sync.currentChord, sync.currentIndex, sync.currentTime, sync.duration, sync.isPlaying, sync.nextChord, sync.seekTo]);

  if (!video?.youtube_video_id) {
    return (
      <section className="rounded-2xl border border-dashed border-white/15 p-8 text-center text-slate-400">
        Selecciona un video para activar el preview sincronizado.
      </section>
    );
  }

  return (
    <section className="space-y-4 rounded-2xl border border-white/10 bg-white/[0.045] p-4 sm:p-5">
      <div>
        <h2 className="text-lg font-semibold text-white">Preview de publicacion</h2>
        <p className="text-sm text-slate-400">Revisa video y sincronizacion; la edicion musical vive en el carril inferior.</p>
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(17rem,1fr)_minmax(20rem,34rem)] lg:items-start">
        <div className="space-y-4 rounded-2xl border border-white/[0.07] bg-black/15 p-3 sm:p-4">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
            <span className="font-semibold uppercase tracking-[0.18em] text-slate-500">Estado del reproductor</span>
            <span className={`rounded-full px-3 py-1 font-semibold ${
              playerError ? "bg-red-400/15 text-red-100" : sync.isPlaying ? "bg-cyan-300/15 text-cyan-100" : playerReady ? "bg-white/10 text-slate-200" : "bg-amber-300/15 text-amber-100"
            }`}>
              {playerError ? "Error" : sync.isPlaying ? "Reproduciendo" : playerReady ? "Listo / pausado" : "Cargando iframe"}
            </span>
          </div>
          {playerError && (
            <p className="rounded-xl border border-red-300/20 bg-red-300/[0.08] p-3 text-sm text-red-100">
              {playerError}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" disabled={controlsDisabled} onClick={() => sync.seekTo(0)} className="grid min-h-11 min-w-11 place-items-center rounded-xl border border-white/10 text-white disabled:opacity-40"><RotateCcw className="h-4 w-4" /></button>
            <button type="button" disabled={controlsDisabled} onClick={() => sync.seekTo(Math.max(0, sync.currentTime - 5))} className="grid min-h-11 min-w-11 place-items-center rounded-xl border border-white/10 text-white disabled:opacity-40"><SkipBack className="h-4 w-4" /></button>
            <button type="button" disabled={controlsDisabled} onClick={() => playerRef.current?.play?.()} className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-cyan-300 px-4 text-sm font-semibold text-slate-950 disabled:opacity-40"><Play className="h-4 w-4" /> Play</button>
            <button type="button" disabled={controlsDisabled} onClick={() => playerRef.current?.pause?.()} className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-white/10 px-4 text-sm text-white disabled:opacity-40"><Pause className="h-4 w-4" /> Pause</button>
            <button type="button" disabled={controlsDisabled} onClick={() => sync.seekTo(sync.currentTime + 5)} className="grid min-h-11 min-w-11 place-items-center rounded-xl border border-white/10 text-white disabled:opacity-40"><SkipForward className="h-4 w-4" /></button>
            <span className="ml-auto self-center font-mono text-sm font-semibold text-cyan-100">{formatTime(sync.currentTime)} / {formatTime(sync.duration || duration)}</span>
          </div>
          <button type="button" disabled={controlsDisabled} onClick={(event) => {
            const bounds = event.currentTarget.getBoundingClientRect();
            sync.seekTo(((event.clientX - bounds.left) / bounds.width) * (sync.duration || duration || 0));
          }} className="relative h-3 w-full overflow-hidden rounded-full bg-white/10 disabled:opacity-40" aria-label="Seek preview">
            <span className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-cyan-300 to-violet-400" style={{ width: `${sync.progress}%` }} />
          </button>
        </div>
        <YouTubePracticePlayer
          ref={playerRef}
          videoId={video.youtube_video_id}
          onReady={() => {
            setPlayerReady(true);
            setPlayerError("");
            sync.syncNow(true);
          }}
          onStateChange={sync.handlePlayerStateChange}
          onError={() => {
            setPlayerReady(false);
            setPlayerError("El video no pudo iniciar en el iframe oficial. Elige otro video embebible para continuar.");
          }}
        />
      </div>
    </section>
  );
};

export default AdminPracticePreview;
